import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  HostListener,
  OnInit,
  OnDestroy,
  TemplateRef,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ConnectedPosition, OverlayPositionBuilder } from '@angular/cdk/overlay';
import { BrnPopover } from '@spartan-ng/brain/popover';
import {
  HlmPopoverComponent,
  HlmPopoverContentComponent,
  HlmPopoverPortalDirective,
} from '../../spartan/popover';
import { BottomSheetComponent } from '../bottom-sheet/bottom-sheet.component';
import { MOBILE_BREAKPOINT } from 'src/app/@core/constants/breakpoints';

export type PopoverTrigger = 'click' | 'hover' | null;
export type PopoverPlacement = 'top' | 'bottom' | 'left' | 'right';
export type PopoverAlign = 'start' | 'center' | 'end';

/** Backward-compat interface matching the PrimeNG Popover API. */
export interface PopoverCompatibility {
  show(event?: any, target?: any): void;
  hide(): void;
  toggle(event?: any): void;
  container?: any;
}

/**
 * Returns CDK connected positions for placement + align.
 * For top/bottom: align controls horizontal alignment (start=left, center, end=right).
 * For left/right: align is ignored.
 */
function getPlacementPositions(
  placement: PopoverPlacement,
  align: PopoverAlign,
): ConnectedPosition[] {
  const h = align === 'start' ? 'start' : align === 'end' ? 'end' : 'center';
  switch (placement) {
    case 'bottom':
      return [
        { originX: h, originY: 'bottom', overlayX: h, overlayY: 'top', offsetX: 0, offsetY: 8 },
        { originX: h, originY: 'top', overlayX: h, overlayY: 'bottom', offsetX: 0, offsetY: -8 },
        { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8, offsetY: 0 },
        { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8, offsetY: 0 },
      ];
    case 'top':
      return [
        { originX: h, originY: 'top', overlayX: h, overlayY: 'bottom', offsetX: 0, offsetY: -8 },
        { originX: h, originY: 'bottom', overlayX: h, overlayY: 'top', offsetX: 0, offsetY: 8 },
        { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8, offsetY: 0 },
        { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8, offsetY: 0 },
      ];
    case 'right':
      return [
        { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8, offsetY: 0 },
        { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8, offsetY: 0 },
        { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetX: 0, offsetY: 8 },
        { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetX: 0, offsetY: -8 },
      ];
    case 'left':
      return [
        { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8, offsetY: 0 },
        { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8, offsetY: 0 },
        { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetX: 0, offsetY: 8 },
        { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetX: 0, offsetY: -8 },
      ];
  }
}

/**
 * Arena popover wrapper (`app-popover`).
 *
 * - Desktop (≥ 1024px) or when `fullscreenOnMobile` is false:
 *   Uses Spartan `hlm-popover` (BrnPopover / CDK overlay) for positioned popovers.
 *
 * - Mobile (< 1024px) when `fullscreenOnMobile` is true (opt-in):
 *   Opens a bottom-sheet via `app-bottom-sheet`.
 *
 * ### Public API (preserved for all existing usages)
 * Content slots: `#trigger`, `#header`, `#body`, `#footer`
 * Inputs: trigger, placement, visible, overlayClickable, disabled, class, width, maxHeight, showScrollbar
 * New inputs: fullscreenOnMobile (default: false)
 * Methods: show(), hide(), toggle()
 * Property: popover (PrimeNG compatibility shim)
 */
@Component({
  selector: 'app-popover',
  standalone: true,
  imports: [
    CommonModule,
    HlmPopoverComponent,
    HlmPopoverContentComponent,
    HlmPopoverPortalDirective,
    BottomSheetComponent,
  ],
  templateUrl: './popover.component.html',
  styleUrls: ['./popover.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PopoverComponent implements OnInit, AfterViewInit, OnDestroy {
  @ContentChild('trigger') triggerTemplate!: TemplateRef<any>;
  @ContentChild('header') headerTemplate?: TemplateRef<any>;
  @ContentChild('body') bodyTemplate!: TemplateRef<any>;
  @ContentChild('footer') footerTemplate?: TemplateRef<any>;

  /** Reference to the spartan HlmPopoverComponent; read as BrnPopover for direct signal access. */
  @ViewChild(HlmPopoverComponent, { read: BrnPopover })
  private brnPopover?: BrnPopover;

  /** Native element of the visible trigger div — passed as `attachTo` for overlay positioning. */
  @ViewChild('triggerRef')
  private triggerRef?: { nativeElement: HTMLElement };

  private readonly overlayPositionBuilder = inject(OverlayPositionBuilder);

  // ── Inputs ──────────────────────────────────────────────────────────────
  readonly trigger = input<PopoverTrigger>('click');
  readonly placement = input<PopoverPlacement>('bottom');
  /**
   * Horizontal alignment when placement is top/bottom.
   * 'end' = overlay's right edge aligns with trigger (use for top-right triggers).
   * 'start' = overlay's left edge aligns with trigger. 'center' = default.
   */
  readonly align = input<PopoverAlign>('center');
  readonly visible = input<boolean>(false);
  readonly overlayClickable = input<boolean>(true);
  readonly disabled = input<boolean>(false);
  readonly class = input<string>('');
  readonly width = input<string>('auto');
  readonly maxHeight = input<string>('auto');
  readonly showScrollbar = input<boolean>(false);
  /**
   * When true and the viewport is narrower than the mobile breakpoint (1024 px),
   * the popover opens as a bottom-sheet overlay instead of an anchored overlay.
   * Default: false (opt-in, preserves backward compatibility).
   */
  readonly fullscreenOnMobile = input<boolean>(false);

  // ── Outputs ──────────────────────────────────────────────────────────────
  readonly visibleChange = output<boolean>();
  readonly onToggle = output<boolean>();

  // ── Mobile state ──────────────────────────────────────────────────────────
  private readonly _windowWidth = signal<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1200,
  );

  readonly isMobile = computed(() => this._windowWidth() < MOBILE_BREAKPOINT);

  readonly useMobileOverlay = computed(
    () => this.fullscreenOnMobile() && this.isMobile(),
  );

  /** Controls the BrnDialog state for the mobile bottom-sheet. */
  readonly mobileDialogState = signal<'open' | 'closed'>('closed');

  // ── Desktop state ─────────────────────────────────────────────────────────
  private readonly _desktopOpen = signal<boolean>(false);
  private _desktopPanelHover = false;
  private _closeTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly _hoverCloseDelayMs = 125;

  /** Bound to `[state]` on `<hlm-popover>`. BrnDialog reacts to changes and opens/closes. */
  readonly desktopState = computed<'open' | 'closed'>(() =>
    this._desktopOpen() ? 'open' : 'closed',
  );

  /** The native trigger element used as the CDK overlay origin (`attachTo`). */
  get triggerEl(): HTMLElement | undefined {
    return this.triggerRef?.nativeElement;
  }

  // ── PrimeNG compatibility shim ─────────────────────────────────────────────
  get popover(): PopoverCompatibility {
    const self = this;
    return {
      show(event?: any, target?: any): void { self.show(event, target); },
      hide(): void { self.hide(); },
      toggle(event?: any): void { self.toggle(event); },
      container: undefined,
    };
  }

  constructor() {
    // Sync the `visible` input → internal desktop state.
    effect(() => {
      const vis = this.visible();
      untracked(() => this._desktopOpen.set(vis));
    });
  }

  ngAfterViewInit(): void {
    // [attachTo]="triggerEl" causes BrnPopover's internal effect to run during the initial
    // CD and build a strategy with withPush(false). We override it in a macrotask (setTimeout 0)
    // which runs after all Angular effects have flushed.
    setTimeout(() => this._applyPositionStrategy(), 0);
  }

  @HostListener('window:resize')
  onResize(): void {
    this._windowWidth.set(window.innerWidth);
  }

  ngOnInit(): void {
    this._windowWidth.set(
      typeof window !== 'undefined' ? window.innerWidth : 1200,
    );
  }

  ngOnDestroy(): void { }

  /**
   * Builds a FlexibleConnectedPositionStrategy anchored to the trigger element and
   * injects it directly into BrnPopover, bypassing BrnPopover's own strategy builder
   * (which uses withPush(false) and cannot be configured from the outside).
   * Safe to call before ngAfterViewInit — guards on brnPopover availability.
   */
  private _applyPositionStrategy(origin?: HTMLElement): void {
    if (this.useMobileOverlay()) return;
    const anchorEl = origin ?? this.triggerEl;
    if (!this.brnPopover || !anchorEl) return;
    const strategy = this.overlayPositionBuilder
      .flexibleConnectedTo(anchorEl)
      .withPositions(getPlacementPositions(this.placement(), this.align()))
      .withPush(true)
      .withViewportMargin(8)
      .withFlexibleDimensions(false);
    this.brnPopover.mutablePositionStrategy.set(strategy);
  }

  // ── Public API ────────────────────────────────────────────────────────────

  show(_event?: any, _target?: any): void {
    this._cancelScheduledClose();
    if (this.disabled()) return;
    if (this.useMobileOverlay()) {
      this.mobileDialogState.set('open');
      this.visibleChange.emit(true);
      this.onToggle.emit(true);
    } else {
      // When called programmatically with an event (no #trigger template), use the
      // event target as the anchor so the overlay appears next to the clicked element.
      const origin =
        (_target instanceof HTMLElement ? _target : null) ??
        (_event?.target instanceof HTMLElement ? (_event.target as HTMLElement) : null) ??
        undefined;
      // Apply strategy synchronously before signalling open.
      // BrnDialog.open() is called asynchronously via effect, so our strategy
      // is guaranteed to be in place when _options() is read.
      this._applyPositionStrategy(origin);
      this._desktopOpen.set(true);
      this.visibleChange.emit(true);
      this.onToggle.emit(true);
    }
  }

  hide(): void {
    this._cancelScheduledClose();
    if (this.useMobileOverlay()) {
      this.mobileDialogState.set('closed');
      this.visibleChange.emit(false);
      this.onToggle.emit(false);
    } else {
      this._desktopOpen.set(false);
      this.visibleChange.emit(false);
      this.onToggle.emit(false);
    }
  }

  toggle(_event?: any): void {
    if (this.disabled()) return;
    if (this.useMobileOverlay()) {
      if (this.mobileDialogState() === 'open') { this.hide(); } else { this.show(_event); }
    } else {
      if (this._desktopOpen()) { this.hide(); } else { this.show(_event); }
    }
  }

  // ── Desktop event handlers ────────────────────────────────────────────────

  /**
   * Called by `stateChanged` output from `<hlm-popover>`.
   * Only emits when the state actually changes (e.g. closed by outside click),
   * to avoid double-emission when `show()` / `hide()` drives the state.
   */
  handleDesktopStateChanged(state: 'open' | 'closed'): void {
    const isOpen = state === 'open';
    if (isOpen !== this._desktopOpen()) {
      this._desktopOpen.set(isOpen);
      this.visibleChange.emit(isOpen);
      this.onToggle.emit(isOpen);
    }
  }

  onDesktopTriggerClick(): void {
    if (!this.disabled()) this.toggle();
  }

  onDesktopTriggerMouseEnter(): void {
    this._cancelScheduledClose();
    if (!this.disabled()) this.show();
  }

  onDesktopTriggerMouseLeave(): void {
    this._scheduleCloseIfNotInteracting();
  }

  onDesktopPanelMouseEnter(): void {
    this._desktopPanelHover = true;
    this._cancelScheduledClose();
  }

  onDesktopPanelMouseLeave(): void {
    this._desktopPanelHover = false;
    this._scheduleCloseIfNotInteracting();
  }

  private _scheduleCloseIfNotInteracting(): void {
    if (this.disabled()) return;
    if (!this._desktopOpen()) return;
    if (this._desktopPanelHover) return;

    this._cancelScheduledClose();
    this._closeTimeoutId = setTimeout(() => {
      this._closeTimeoutId = null;
      if (!this._desktopPanelHover) this.hide();
    }, this._hoverCloseDelayMs);
  }

  private _cancelScheduledClose(): void {
    if (this._closeTimeoutId) {
      clearTimeout(this._closeTimeoutId);
      this._closeTimeoutId = null;
    }
  }

  // ── Mobile event handlers ────────────────────────────────────────────────

  handleMobileStateChanged(state: 'open' | 'closed'): void {
    const isOpen = state === 'open';
    this.mobileDialogState.set(state);
    this.visibleChange.emit(isOpen);
    this.onToggle.emit(isOpen);
  }

  onMobileTriggerClick(): void {
    if (!this.disabled()) this.toggle();
  }
}
